import { Link } from "react-router-dom";
import { ArrowRight, Waves, Coffee, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1622323589314-d46bced463a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxkYXl0b25hJTIwYmVhY2glMjBwaWVyJTIwb2NlYW4lMjBzdW5zZXR8ZW58MHx8fHwxNzcyMTI1MjkwfDA&ixlib=rb-4.1.0&q=85",
  room1: "https://images.unsplash.com/photo-1763616310394-b8ed0a3318c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb2xvcmZ1bCUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzIxMjUyOTF8MA&ixlib=rb-4.1.0&q=85",
  room2: "https://images.unsplash.com/photo-1687941544146-351d41b57cbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb2xvcmZ1bCUyMGJlZHJvb20lMjBpbnRlcmlvciUyMGRlc2lnbnxlbnwwfHx8fDE3NzIxMjUyOTF8MA&ixlib=rb-4.1.0&q=85",
  room3: "https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  about: "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const features = [
  {
    icon: Waves,
    title: "STEPS FROM THE SHORE",
    desc: "Wake up to the sound of waves. Our beachfront location puts you steps from Daytona's famous coastline.",
  },
  {
    icon: Coffee,
    title: "BREAKFAST INCLUDED",
    desc: "Start your day with locally sourced, freshly prepared breakfast. Coffee that'll make you question everything.",
  },
  {
    icon: Sun,
    title: "TECH EVERYWHERE",
    desc: "Every room is wired for the future. Smart home meets beach house in a space that feels like tomorrow.",
  },
];

const rooms = [
  { name: "THE WAVE RIDER", price: "$149", img: IMAGES.room1 },
  { name: "THE SANDCASTLE", price: "$189", img: IMAGES.room2 },
  { name: "THE LIGHTHOUSE", price: "$229", img: IMAGES.room3 },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO SECTION */}
      <section
        data-testid="hero-section"
        className="relative min-h-[90vh] overflow-hidden bg-ink"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.hero})` }}
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="noise-overlay absolute inset-0" />

        <div className="relative z-10 flex min-h-[90vh] items-center">
          <div className="mx-auto max-w-7xl px-6 py-20">
            <div className="max-w-3xl">
              <p
                className="font-marker text-teal text-base md:text-lg animate-fade-up"
                style={{ animationDelay: "0.1s" }}
              >
                Daytona Beach, Florida
              </p>
              <h1
                className="mt-4 font-anton text-5xl leading-none tracking-tight text-sand sm:text-7xl lg:text-8xl stencil-text animate-slam-in"
              >
                SEAS
                <br />
                THE BAY
              </h1>
              <p
                className="mt-6 max-w-lg font-courier text-base text-sand/80 leading-relaxed opacity-0 animate-fade-up"
                style={{ animationDelay: "0.3s" }}
              >
                A boutique bed & breakfast where the ocean meets technology.
                Not your grandmother's B&B. Unless your grandmother was Ada Lovelace.
              </p>

              <div
                className="mt-10 flex flex-wrap gap-4 opacity-0 animate-fade-up"
                style={{ animationDelay: "0.5s" }}
              >
                <Link to="/contact">
                  <Button
                    data-testid="hero-book-now-btn"
                    className="bg-coral text-white font-anton tracking-widest text-sm px-8 py-6 hover:-translate-y-1 hover:shadow-brutal-teal transition-all duration-200 rounded-none border-2 border-coral"
                  >
                    SECURE YOUR SPOT <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/rooms">
                  <Button
                    data-testid="hero-view-rooms-btn"
                    variant="outline"
                    className="font-anton tracking-widest text-sm px-8 py-6 border-2 border-sand text-sand bg-transparent hover:bg-sand hover:text-ink transition-all duration-200 rounded-none"
                  >
                    VIEW ROOMS
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Stencil accent */}
        <div className="absolute bottom-8 right-8 hidden lg:block">
          <div className="stamp-seal border-teal text-teal">
            <span className="font-anton text-xs tracking-widest block text-center leading-tight">
              BED &<br />BREAKFAST
            </span>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section data-testid="features-section" className="relative bg-sand py-20">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="font-anton text-3xl tracking-wider text-ink stencil-text sm:text-4xl">
            WHY STAY WITH US
          </h2>
          <div className="mt-2 h-1 w-20 bg-coral" />

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {features.map((feature, i) => (
              <div
                key={i}
                className="card-brutal bg-white p-8"
              >
                <feature.icon className="h-8 w-8 text-teal" />
                <h3 className="mt-4 font-anton text-lg tracking-wider text-ink">
                  {feature.title}
                </h3>
                <p className="mt-3 font-courier text-sm leading-relaxed text-ink/70">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROOMS PREVIEW */}
      <section data-testid="rooms-preview-section" className="relative bg-ink py-20">
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-marker text-coral text-sm">Where you'll crash</p>
              <h2 className="mt-2 font-anton text-3xl tracking-wider text-sand stencil-text sm:text-4xl">
                THE ROOMS
              </h2>
            </div>
            <Link
              to="/rooms"
              data-testid="rooms-preview-view-all"
              className="font-outfit text-sm text-teal tracking-wider uppercase hover:underline flex items-center gap-1"
            >
              VIEW ALL ROOMS <ArrowRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {rooms.map((room, i) => (
              <div
                key={i}
                className="group overflow-hidden border-2 border-sand/20 bg-sand/5 transition-all hover:border-teal"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={room.img}
                    alt={room.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 grayscale group-hover:grayscale-0"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-anton text-lg tracking-wider text-sand">
                      {room.name}
                    </h3>
                    <span className="font-marker text-teal text-lg">
                      {room.price}
                    </span>
                  </div>
                  <p className="mt-1 font-courier text-xs text-sand/50">per night</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section data-testid="about-preview-section" className="relative bg-sand py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="font-marker text-coral text-sm">Our Story</p>
              <h2 className="mt-2 font-anton text-3xl tracking-wider text-ink stencil-text sm:text-4xl">
                NOT YOUR TYPICAL B&B
              </h2>
              <div className="mt-2 h-1 w-20 bg-teal" />
              <p className="mt-6 font-courier text-base leading-relaxed text-ink/80">
                Born from a love of the ocean and a healthy disrespect for boring hotel rooms,
                Seas The Bay is where Daytona Beach gets interesting. We took a classic
                beachfront property and turned it into a tech-forward retreat — part surf shack,
                part innovation lab, all heart.
              </p>
              <p className="mt-4 font-courier text-base leading-relaxed text-ink/80">
                Every room tells a story. Every wall has something to say.
                And the breakfast? Don't even get us started.
              </p>
              <Link
                to="/about"
                data-testid="about-preview-read-more"
                className="mt-8 inline-block"
              >
                <Button
                  className="bg-ink text-sand font-anton tracking-widest text-sm px-8 py-5 hover:-translate-y-1 hover:shadow-brutal-teal transition-all duration-200 rounded-none"
                >
                  READ OUR STORY <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="polaroid rotate-2">
                <img
                  src={IMAGES.about}
                  alt="Seas The Bay exterior"
                  className="aspect-[4/3] w-full object-cover"
                />
                <p className="mt-2 font-marker text-ink text-xs text-center">
                  Home sweet home
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section
        data-testid="cta-section"
        className="relative overflow-hidden bg-teal py-16"
      >
        <div className="noise-overlay absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center">
          <h2 className="font-anton text-3xl tracking-wider text-ink stencil-text sm:text-5xl">
            READY TO SEAS THE DAY?
          </h2>
          <p className="mt-4 font-courier text-base text-ink/80">
            Book your stay and make some waves.
          </p>
          <Link to="/contact" className="mt-8 inline-block">
            <Button
              data-testid="cta-book-btn"
              className="bg-ink text-sand font-anton tracking-widest text-sm px-10 py-6 hover:-translate-y-1 hover:shadow-brutal-coral transition-all duration-200 rounded-none border-2 border-ink"
            >
              BOOK YOUR STAY
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
